// Cycle-16 event-message row. Mirrors `PingEventMessage` (iOS-A) /
// `EventMessage` (AND-A). Renders a CENTERED system row flanked by
// horizontal divider lines, per Figma `chat-event-single` (`3412:5334`),
// `multiple_collapsed` (`3412:5340`) and `multiple_expanded`
// (`3412:5349`). Pure controlled view (expand state optionally controlled
// via `expanded` / `onToggleExpand`, else managed internally).

import { ChevronDown, ChevronUp } from 'lucide-react-native';
import React, { useState } from 'react';
import { Image, type ImageSourcePropType, Pressable, StyleSheet, Text, type TextStyle, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';
import type { EventMessageType } from './EventMessageType';
import type { EventMessageVariant } from './EventMessageVariant';

export { EVENT_MESSAGE_VARIANTS, type EventMessageVariant } from './EventMessageVariant';

// Retained for source compatibility — the secondary action is no longer
// rendered (the Figma event row has no action button), but the prop is
// still accepted so callers keep compiling.
export interface EventMessageSecondaryAction {
  label: string;
  onPress: () => void;
}

// One individual entry inside a `multiple_expanded` group.
export interface EventMessageEntry {
  // Stable key + a11y text for the row.
  title: string;
  // The emphasized/quoted trailing segment (rendered bold), e.g. `"Nabe"`.
  emphasis?: string | undefined;
  avatar?: ImageSourcePropType | undefined;
}

export interface EventMessageProps {
  kind: EventMessageType;
  variant: EventMessageVariant;
  title: string;
  detail?: string | undefined;
  timestamp: Date;
  secondary?: EventMessageSecondaryAction | undefined;
  testID?: string | undefined;
  // Optional avatar for the single-event row (16x16, radius 4).
  avatar?: ImageSourcePropType | undefined;
  // The emphasized/quoted trailing segment of `title`, rendered bold.
  emphasis?: string | undefined;
  // Collapsed/expanded group of events. When present (length > 1) the row
  // renders the `multiple_*` layout instead of the single layout.
  events?: ReadonlyArray<EventMessageEntry> | undefined;
  // Avatars shown overlapping in the group header (defaults to the first
  // two `events` avatars).
  groupAvatars?: ReadonlyArray<ImageSourcePropType | undefined> | undefined;
  // Group header label, e.g. `4 group updates`. Defaults to
  // `${events.length} group updates`.
  groupLabel?: string | undefined;
  // Controlled expand state. When omitted the component manages its own.
  expanded?: boolean | undefined;
  onToggleExpand?: (() => void) | undefined;
}

const AVATAR_SIZE = 16;

function Avatar({ source }: { source?: ImageSourcePropType | undefined }) {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];
  if (source) {
    return <Image source={source} style={styles.avatar} accessibilityIgnoresInvertColors />;
  }
  return <View style={[styles.avatar, { backgroundColor: colors.surface.elevated }]} />;
}

export const EventMessage = React.memo<EventMessageProps>((props) => {
  const {
    variant,
    title,
    detail,
    testID,
    avatar,
    emphasis,
    events,
    groupAvatars,
    groupLabel,
    expanded,
    onToggleExpand,
  } = props;

  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const [internalExpanded, setInternalExpanded] = useState(variant === 'expanded');
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;

  // Figma 7038:2274 — flanking divider lines are #222429 (border.subtle)
  // and the event text is #6B7280 (text.muted), Lato 400 12/17.
  const lineStyle: ViewStyle = { backgroundColor: colors.border.subtle };
  const textStyle: TextStyle = { color: colors.text.muted };

  const isGroup = Array.isArray(events) && events.length > 1;

  // ---- single event ------------------------------------------------------
  if (!isGroup) {
    return (
      <View
        style={styles.row}
        accessibilityRole="text"
        accessibilityLabel={detail ? `${title} ${detail}` : title}
        testID={testID}
      >
        <View style={[styles.line, lineStyle]} />
        <View style={styles.singleContent}>
          {avatar !== undefined ? <Avatar source={avatar} /> : null}
          <Text style={[styles.text, textStyle]}>
            {title}
            {emphasis ? <Text style={styles.emphasis}> {emphasis}</Text> : null}
            {/* The trailing detail carries the actual changed value (e.g.
                `to "New name"` for a rename/topic event). Without rendering
                it, every member saw "Alice renamed the group" with the new
                name missing. */}
            {detail ? <Text>{` ${detail}`}</Text> : null}
          </Text>
        </View>
        <View style={[styles.line, lineStyle]} />
      </View>
    );
  }

  // ---- multiple events (collapsed / expanded) ----------------------------
  const entries = events as ReadonlyArray<EventMessageEntry>;
  const headerAvatars =
    groupAvatars && groupAvatars.length > 0
      ? groupAvatars.slice(0, 2)
      : entries.slice(0, 2).map((e) => e.avatar);
  const label = groupLabel ?? `${entries.length} group updates`;
  const Chevron = isExpanded ? ChevronUp : ChevronDown;

  const handleToggle = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
    if (!isControlled) {
      setInternalExpanded((prev) => !prev);
    }
  };

  return (
    <View testID={testID}>
      <View style={styles.row}>
        <View style={[styles.line, lineStyle]} />
        <Pressable
          onPress={handleToggle}
          accessibilityRole="button"
          accessibilityState={{ expanded: isExpanded }}
          accessibilityLabel={label}
          style={styles.groupHeader}
        >
          <View style={styles.avatarStack}>
            {headerAvatars.map((src, i) => (
              <View key={i} style={i > 0 ? styles.avatarOverlap : undefined}>
                <Avatar source={src} />
              </View>
            ))}
          </View>
          <Text style={[styles.text, textStyle]}>{label}</Text>
          <Chevron size={16} color={colors.text.muted} />
        </Pressable>
        <View style={[styles.line, lineStyle]} />
      </View>
      {isExpanded ? (
        <View style={styles.expandedList}>
          {entries.map((entry, i) => (
            <View key={i} style={styles.expandedItem}>
              <Avatar source={entry.avatar} />
              <Text style={[styles.text, textStyle]}>
                {entry.title}
                {entry.emphasis ? (
                  <Text style={styles.emphasis}> {entry.emphasis}</Text>
                ) : null}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
});

EventMessage.displayName = 'EventMessage';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
  },
  line: {
    flex: 1,
    height: 1,
  },
  singleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingTop: 4,
    paddingBottom: 2,
    paddingHorizontal: tokens.spacing.sm,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingTop: 4,
    paddingBottom: 2,
    paddingHorizontal: tokens.spacing.sm,
  },
  avatarStack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarOverlap: {
    marginLeft: -2,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 4,
  },
  text: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '400',
  },
  emphasis: {
    fontWeight: '700',
  },
  expandedList: {
    paddingLeft: tokens.spacing.lg,
    gap: tokens.spacing.xs,
    paddingTop: tokens.spacing.xs,
  },
  expandedItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
});
